import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Box, Button, styled, ThemeProvider, useMediaQuery } from '@mui/material';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import theme from '../theme';
import MainHeader from './components/MainHeader';
import WelcomeHeader from './components/WelcomeHeader';

import '@fontsource/inter/400.css';
import '@fontsource/inter/700.css';

import { API_E2E_SUBMIT_MARKETING_LINKS } from 'src/api/e2e-request-objects';
import { apiRequest } from 'src/api/request-handler';
import { useAuth } from 'src/hooks/use-auth';
import { fsSetIdentify, fsStart } from 'src/utils/fullstory';

interface LayoutProps {
  children: ReactNode;
}

export enum LocalStorageE {
  'mLink' = 'mLink',
  'cfaConfigId' = 'cfaConfigId',
  'source' = 'source',
  'formChoice' = 'formChoice',
  'notification' = 'notification',
  'paymentType' = 'paymentType',
}

const LayoutContainer = styled(Box)<{ isWelcome: boolean; isMobile: boolean }>(({ isWelcome, isMobile }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  width: '100%',
  ...(isWelcome &&
    isMobile && {
      backgroundImage: `url('/assets/e2e-redesign/images/welcome-header.png')`,
    }),
}));

interface PotentialParamsToSave extends Record<string, string | undefined> {
  store_name?: string;
  store_uuid?: string;
  e2e_form_id?: string;
}
const MainLayout: FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const isWelcome = location.pathname.endsWith('/welcome');
  const [fsInit, setFsInit] = useState<boolean>(false);
  const params = useParams<PotentialParamsToSave>();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Synchronously capture and persist mLink before first render.
  // This guarantees it is in storage before any button click can fire,
  // eliminating the useEffect deferral race condition.
  const mLinkParam = searchParams.get('mLink');
  const qrParam = searchParams.get('qr');
  if (mLinkParam && params?.e2e_form_id) {
    window.localStorage.setItem(LocalStorageE.mLink, mLinkParam);
    window.sessionStorage.setItem(LocalStorageE.mLink, mLinkParam);
  }
  const showUserName = () => {
    // TODO E2E P0 - make this look better.. need to get with vincent
    if (user) {
      return (
        <p>
          {user.first_name} {user.last_name}
        </p>
      );
    }
    return null;
  };

  useEffect(() => {
    if (process.env.REACT_APP_ENVIRONMENT !== 'production') {
      if (location.pathname.includes('welcome')) {
        localStorage.setItem('og_path', location.pathname);
      }
    }
  }, []);

  useEffect(() => {
    if (isWelcome) {
      localStorage.removeItem(LocalStorageE.formChoice);
      localStorage.removeItem('hasSeenPrefillModal');
    }
  }, [isWelcome]);

  const submitCfaMarketingLink = async (cfaId: string, mklId: string, qr?: string | null) => {
    // localStorage and sessionStorage already written synchronously at component init.
    // This call exists solely to record the marketing link hit on the server.
    try {
      await apiRequest(API_E2E_SUBMIT_MARKETING_LINKS, {
        body: { cfa_config_id: cfaId, marketing_links_id: mklId, qr },
      });
    } catch (e) {
      console.log('failed to submit cfa mlink', e);
    }
  };

  useEffect(() => {
    if (params?.e2e_form_id && mLinkParam) {
      submitCfaMarketingLink(params.e2e_form_id, mLinkParam, qrParam);
    }
  }, []);

  useEffect(() => {
    const sourceId = searchParams.get(LocalStorageE.source);
    const paymentType = searchParams.get(LocalStorageE.paymentType);
    // - Set params if present or changed)
    if (sourceId && window.localStorage.getItem(LocalStorageE.source) !== sourceId) {
      window.localStorage.setItem(LocalStorageE.source, sourceId);
    }
    if (paymentType) {
      if (window.localStorage.getItem(LocalStorageE.paymentType) !== paymentType) {
        window.localStorage.setItem(LocalStorageE.paymentType, paymentType);
      }
    } 
  }, [searchParams]);

  // Handles 'provider-response' parameter  
  useEffect(() => {
    const notification = searchParams.get('notification');
    if (notification === 'provider-response') {
      window.localStorage.setItem(LocalStorageE.notification, 'provider-response');
    } else {
      window.localStorage.removeItem(LocalStorageE.notification);
    }
  }, [searchParams]);

  useEffect(() => {
    const set = fsStart();
    if (set) {
      setFsInit(set);
    }
  }, []);

  useEffect(() => {
    if (fsInit && user && isAuthenticated && user.id && user.email) {
      // init full story once user has logged in
      fsSetIdentify(user);
    }
  }, [user, fsInit]);

  const handleResetCFA = () => {
    const path = localStorage.getItem('og_path');
    if (typeof path === 'string') {
      nav(path);
    }
  };

  const showResetButton = () => {
    if (process.env.REACT_APP_ENVIRONMENT !== 'production') {
      return <Button onClick={handleResetCFA}>Reset Form</Button>;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      {/* TODO ADD BACK AFTER LENDINGCLUB REVIEW */}
      {/* {showResetButton()} */}
      <LayoutContainer isWelcome={isWelcome} isMobile={isMobile}>
        {isWelcome && isMobile ? <WelcomeHeader /> : <MainHeader />}
        {/* {isAuthenticated && showUserName()} */}
        <Box sx={{ flexGrow: 1 }}>{children}</Box>
      </LayoutContainer>
    </ThemeProvider>
  );
};

export default MainLayout;
